import { CheckCheck, ExternalLink, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useNotification } from '@/context/NotificationProvider';
import { fullDate } from '@/lib/datetime';
import { notifyMutationError } from '@/lib/errors';

import type { AmostraResumo, Incidente } from '../../../api/monitoramento';
import {
  useIncidente,
  useRegistrarAckIncidente,
} from '../../../hooks/useMonitoramentoQuery';
import { MonitoramentoStatusPill } from './MonitoramentoStatusPill';

interface Props {
  incidenteId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function IncidenteDialog({ incidenteId, open, onOpenChange }: Props) {
  const { data: incidente, isLoading, error } = useIncidente(incidenteId);
  const ackMutation = useRegistrarAckIncidente();
  const { showSuccess, showError } = useNotification();

  const handleAck = async () => {
    if (!incidenteId) return;
    try {
      await ackMutation.mutateAsync(incidenteId);
      showSuccess('Incidente reconhecido.');
      onOpenChange(false);
    } catch (exc) {
      notifyMutationError(exc, showError);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {incidente ? `Incidente #${incidente.id} — ${incidente.alvo}` : 'Carregando…'}
          </DialogTitle>
          {incidente && (
            <DialogDescription>
              Aberto em {fullDate(incidente.aberto_em)} ·{' '}
              Severidade: <strong>{incidente.severidade}</strong>
              {incidente.fechado_em &&
                ` · Fechado em ${fullDate(incidente.fechado_em)}`}
            </DialogDescription>
          )}
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center gap-2 py-6 text-gray-500">
            <Loader2 className="size-4 animate-spin" /> Carregando detalhes…
          </div>
        )}

        {error && !isLoading && (
          <div className="py-4 text-sm text-red-600">
            Falha ao carregar incidente.
          </div>
        )}

        {incidente && (
          <div className="space-y-3 text-sm">
            {incidente.razao_abertura && (
              <div>
                <div className="font-medium text-gray-700 dark:text-gray-300">
                  Razão
                </div>
                <pre className="whitespace-pre-wrap rounded bg-gray-50 p-2 text-xs dark:bg-gray-900">
                  {incidente.razao_abertura}
                </pre>
              </div>
            )}
            {incidente.ultimo_erro && (
              <div>
                <div className="font-medium text-gray-700 dark:text-gray-300">
                  Último erro
                </div>
                <pre className="whitespace-pre-wrap rounded bg-gray-50 p-2 text-xs dark:bg-gray-900">
                  {incidente.ultimo_erro}
                </pre>
              </div>
            )}
            {incidente.falhas_consecutivas !== null && (
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  Falhas consecutivas:{' '}
                </span>
                {incidente.falhas_consecutivas}
              </div>
            )}
            {incidente.ack_por && (
              <div>
                <span className="font-medium text-gray-700 dark:text-gray-300">
                  Ack por:{' '}
                </span>
                {incidente.ack_por} em{' '}
                {incidente.ack_em && fullDate(incidente.ack_em)}
              </div>
            )}
            <AmostrasSection amostras={incidente.amostras ?? []} />
          </div>
        )}

        <DialogFooter>
          {incidente && (
            <Button variant="outline" asChild>
              <Link to={construirLinkLogs(incidente)} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-2 size-4" /> Ver logs do período
              </Link>
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          {incidente && !incidente.ack_por && (
            <Button onClick={handleAck} disabled={ackMutation.isPending}>
              {ackMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 size-4 animate-spin" /> Registrando…
                </>
              ) : (
                <>
                  <CheckCheck className="mr-2 size-4" /> Registrar ack
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Monta o link para a aba Logs da Observabilidade já filtrado por:
 * - termo de busca = alvo do incidente (cobre logs do componente afetado);
 * - período = a faixa que melhor cobre o intervalo do incidente (1h/6h/24h/7d);
 * - level = ALL (para não esconder INFO/WARN que ajudem no diagnóstico).
 *
 * Abre em nova aba para preservar o contexto de monitoramento.
 */
function construirLinkLogs(incidente: Incidente): string {
  const aberto = new Date(incidente.aberto_em).getTime();
  const fim = incidente.fechado_em ? new Date(incidente.fechado_em).getTime() : Date.now();
  const duracaoMs = Math.max(0, fim - aberto);
  // Faixas do componente ObservabilityFilterBar: 1h, 6h, 24h, 7d.
  // Escolhemos a menor faixa que cobre a duração do incidente.
  const HORA = 60 * 60 * 1000;
  let period: '1h' | '6h' | '24h' | '7d';
  if (duracaoMs <= HORA) period = '1h';
  else if (duracaoMs <= 6 * HORA) period = '6h';
  else if (duracaoMs <= 24 * HORA) period = '24h';
  else period = '7d';
  const params = new URLSearchParams({
    tab: 'logs',
    search: incidente.alvo,
    period,
    level: 'ALL',
  });
  return `/app/admin/observabilidade?${params.toString()}`;
}

function AmostrasSection({ amostras }: { amostras: AmostraResumo[] }) {
  if (amostras.length === 0) {
    return (
      <div>
        <div className="font-medium text-gray-700 dark:text-gray-300">Amostras do intervalo</div>
        <div className="text-xs italic text-gray-500">Sem amostras registradas no intervalo.</div>
      </div>
    );
  }
  // Ordena cronologicamente (mais antiga → mais recente) para que a leitura
  // siga a linha do tempo do incidente: falha → recuperação. Comparação direta
  // de string ISO-8601 (lexicográfica == cronológica) — evita qualquer
  // ambiguidade de parsing por timezone. O backend devolve DESC (limit 100
  // por janela); aqui re-ordenamos sem depender do contrato.
  const ordenadas = [...amostras].sort((a, b) =>
    a.observado_em.localeCompare(b.observado_em),
  );
  return (
    <div>
      <div className="mb-2 font-medium text-gray-700 dark:text-gray-300">
        Amostras do intervalo ({ordenadas.length})
      </div>
      <div className="max-h-64 overflow-auto rounded border border-gray-200 text-xs dark:border-gray-800">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-2 py-1 text-left font-medium">Observado</th>
              <th className="px-2 py-1 text-left font-medium">Status</th>
              <th className="px-2 py-1 text-right font-medium">Latência</th>
              <th className="px-2 py-1 text-left font-medium">Erro</th>
            </tr>
          </thead>
          <tbody>
            {ordenadas.map((a, idx) => (
              <tr key={idx} className="border-t border-gray-100 dark:border-gray-800">
                <td className="px-2 py-1 font-mono">
                  {fullDate(a.observado_em)}
                </td>
                <td className="px-2 py-1">
                  <MonitoramentoStatusPill status={a.status} />
                </td>
                <td className="px-2 py-1 text-right font-mono">
                  {a.latencia_ms !== null ? `${a.latencia_ms} ms` : '—'}
                </td>
                <td className="px-2 py-1 font-mono text-gray-600 dark:text-gray-400">
                  {a.codigo_erro ?? ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
